import BaseLayer from './BaseLayer';
import { pzgikAttribution } from './attribution';
import { getMapLegendRegistry } from '../core/map-legend-registry';

const LEGEND_KEY = 'mesh-coverage';
const LEGEND_OWNER = 'widoki3d';

export default class MeshCoverageLayer extends BaseLayer {
    static ID = 'widoki3d';
    static MIN_ZOOM = 0;

    getLayerIds() { return ['mesh-coverage-poland', 'mesh-coverage-mesh']; }

    syncVisibility() {
        if (!this.isDesired()) { this.clear(); this.setActive(false); return; }
        if (!this.getMap().getSource(this.getSourceId()) && !this.loading && !this.failed) this.load();
        this.updateDisabledState();
    }

    showLegend(message) {
        const map = this.getMap();
        const registry = getMapLegendRegistry(map);
        this.legend = registry.show(LEGEND_KEY, LEGEND_OWNER, {
            className: 'ms-mesh-legend',
            ariaLabel: 'Legenda dostępności widoków trójwymiarowych',
            role: 'status',
            render: legend => {
                const title = document.createElement('strong');
                title.textContent = 'Widoki 3D';
                legend.append(title);
                if (message) {
                    const text = document.createElement('p');
                    text.textContent = message;
                    legend.append(text);
                    if (this.failed) {
                        const retry = document.createElement('button');
                        retry.className = 'ms-button ms-button--secondary';
                        retry.textContent = 'Spróbuj ponownie';
                        retry.onclick = () => this.load();
                        legend.append(retry);
                    }
                    return;
                }
                [['mesh', 'Szczegółowe modele trójwymiarowe'], ['simplified', 'Uproszczone modele budynków']].forEach(([kind, label]) => {
                    const row = document.createElement('div');
                    const swatch = document.createElement('span');
                    swatch.className = 'ms-mesh-legend__swatch ms-mesh-legend__swatch--' + kind;
                    row.append(swatch, document.createTextNode(label));
                    legend.append(row);
                });
                const help = document.createElement('details');
                const summary = document.createElement('summary');
                summary.textContent = 'Jak rozumieć pokrycie?';
                const copy = document.createElement('p');
                copy.textContent = 'Zielony obszar to zasięg arkuszy opublikowanych przez Geoportal. Model jest pobierany dopiero w symulacji. Pozostała Polska korzysta z dostępnych danych budynków Ewidencji Gruntów i Budynków; wysokość jest szacowana z liczby kondygnacji. Kolory pokazują rodzaj dostępnych danych o budynkach. Kliknij miejsce, aby uruchomić symulację. Odświeżenie skorowidza: ' + new Date(this.metadata.updated_at).toLocaleDateString('pl-PL') + '.';
                help.append(summary, copy);
                legend.append(help);
            }
        });
    }

    async load() {
        if (!this.isDesired()) return;
        if (this.abort) this.abort.abort();
        const request = new AbortController();
        this.abort = request;
        this.loading = true;
        this.failed = false;
        this.showLegend('Wczytywanie lokalnego pokrycia…');
        try {
            const response = await fetch(this.config.baseUrl + 'api/widoki-3d', { signal: request.signal });
            if (!response.ok) throw new Error('Skorowidz jest chwilowo niedostępny.');
            const metadata = await response.json();
            if (!this.isDesired() || request.signal.aborted) return;
            this.metadata = metadata;
            if (!this.tileErrorHandler) {
                this.tileErrorHandler = event => {
                    if (event.sourceId === this.getSourceId() && this.isDesired()) {
                        this.failed = true;
                        this.showLegend('Nie udało się wczytać części pokrycia. Brak koloru nie oznacza braku modeli.');
                    }
                };
                this.getMap().on('error', this.tileErrorHandler);
            }
            this.removeLayerFromMap();
            const map = this.getMap();
            map.addSource(this.getSourceId(), { type: 'vector', tiles: metadata.tiles, minzoom: 0, maxzoom: 14,
                attribution: pzgikAttribution('pokrycie modeli trójwymiarowych; granica Polski z Państwowego Rejestru Granic') });
            const before = (map.getStyle().layers || []).find(layer => layer.type === 'symbol' || String(layer.source || '').startsWith('src-'));
            [['poland', '#9bd5ee'], ['mesh', '#46aa68']].forEach(([kind, color]) => {
                map.addLayer({ id: 'mesh-coverage-' + kind, type: 'fill', source: this.getSourceId(),
                    'source-layer': 'coverage', filter: ['==', ['get', 'kind'], kind],
                    paint: { 'fill-color': color, 'fill-opacity': kind === 'mesh' ? 0.45 : 0.25 } }, before && before.id);
            });
            this.setActive(true);
            this.showLegend();
        } catch (error) {
            if (request.signal.aborted) return;
            this.failed = true;
            this.setActive(false);
            this.showLegend('Nie udało się wczytać pokrycia. Nie oznacza to braku modeli.');
        } finally {
            if (this.abort === request) this.loading = false;
        }
    }

    clear() {
        if (this.abort) this.abort.abort();
        this.loading = false;
        this.failed = false;
        this.removeLayerFromMap();
        if (this.tileErrorHandler) this.getMap().off('error', this.tileErrorHandler);
        this.tileErrorHandler = null;
        const registry = getMapLegendRegistry(this.getMap());
        if (registry) registry.hide(LEGEND_KEY, LEGEND_OWNER);
        this.legend = null;
    }
}
